import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { TranslateService } from "@ngx-translate/core";
import { merge } from "rxjs";

import {
  DeepfakeFailureReasonEnum,
  DeepfakeStatusEnum
} from "../../core-ui.enums";
import {
  DEEPFAKE_PENDING_STATES,
  DeepfakeCheckState,
  IDeepfakeCheck,
  IDeepfakeScanResult
} from "../../models";
import { DeepfakeService } from "../../services/deepfake.service";

/** The prominent label for a verdict. Never "real" / "fake" alone. */
export function verdictLabel(status: string): string {
  switch (status as DeepfakeStatusEnum) {
    case DeepfakeStatusEnum.authentic:
      return "No signs of manipulation";
    case DeepfakeStatusEnum.fake:
      return "Likely synthetic media";
    case DeepfakeStatusEnum.suspicious:
      return "Possible signs of manipulation";
    case DeepfakeStatusEnum.notApplicable:
      return "No applicable check for this media";
    case DeepfakeStatusEnum.unableToEvaluate:
      return "Could not evaluate this media";
    default:
      return "Media authenticity";
  }
}

/**
 * The line shown while a check is running, one per step of the procedure.
 *
 * `recording` names the media so the agent knows why the wait is as long as
 * the admin's clip length: a still is instant, a clip is not.
 */
export function runningLabel(
  state: string,
  mediaKind?: IDeepfakeCheck["mediaKind"]
): string {
  switch (state as DeepfakeCheckState) {
    case "submitted":
      return "Preparing the authenticity check...";
    case "listening":
      return "Waiting for the participant to speak...";
    case "recording":
      switch (mediaKind) {
        case "video":
          return "Recording a video clip...";
        case "audio":
          return "Recording an audio clip...";
        case "image+audio":
          return "Capturing a frame and an audio clip...";
        default:
          return "Capturing a frame...";
      }
    case "uploading":
      return "Sending the media for analysis...";
    case "analysing":
      return "Analysing the media...";
    default:
      return "Checking media authenticity...";
  }
}

/** Distinct copy per failure reason — an operational problem is not a verdict. */
export function unavailableLabel(reason: string): string {
  switch (reason as DeepfakeFailureReasonEnum) {
    case DeepfakeFailureReasonEnum.quotaExceeded:
      return "Authenticity check unavailable: the monthly allowance is used up";
    case DeepfakeFailureReasonEnum.planUnsupported:
      return "Authenticity check unavailable: this media type is not included in the current plan";
    case DeepfakeFailureReasonEnum.rateLimited:
      return "Authenticity check unavailable: too many checks at once, try again shortly";
    case DeepfakeFailureReasonEnum.pollTimeout:
      return "Taking longer than usual: the result will appear here if Reality Defender answers later";
    case DeepfakeFailureReasonEnum.notConfigured:
    case DeepfakeFailureReasonEnum.unauthorized:
      return "Authenticity check is not configured for this application";
    case DeepfakeFailureReasonEnum.invalidMedia:
    case DeepfakeFailureReasonEnum.mediaPreparationFailed:
      return "Authenticity check could not use the captured media";
    default:
      return "Authenticity check could not be completed";
  }
}

/**
 * A scan that says nothing about the participant: Reality Defender found
 * nothing it could judge (NOT_APPLICABLE — e.g. under 1.5 s of speech once
 * silence is removed), could not judge it, or the scan failed outright.
 */
function isUnjudged(result: IDeepfakeScanResult): boolean {
  return (
    !result.status ||
    result.status === DeepfakeStatusEnum.notApplicable ||
    result.status === DeepfakeStatusEnum.unableToEvaluate
  );
}

/**
 * Reality Defender's NOT_APPLICABLE reason codes, in the agent's words. The
 * set is the documented one (Media Detail → "Response Details"); a code not
 * listed here falls back to RD's own short label.
 */
const UNJUDGED_REASONS: Record<string, string> = {
  duration: "not enough speech",
  detected: "only a dial tone or music",
  "cross-talk": "more than one person speaking",
  quality: "too much background noise",
  language: "a language the check does not support",
  relevance: "no face in view"
};

/** At most this many reasons go into the headline; the scan line lists them all. */
const MAX_UNJUDGED_REASONS = 2;

/**
 * Why a scan was not judged, when RD said. The code is sometimes RD's whole
 * raw reason ("duration: silence removed audio too short (<1.5s)"), so the
 * part before the colon is the key. RD can give several reasons: the
 * distinct ones, in its order, each a translation key of its own. A failed
 * scan or UNABLE_TO_EVALUATE carries no reason and yields none.
 */
export function unjudgedReasons(
  result: IDeepfakeScanResult | undefined
): string[] {
  const labels = (result?.reasons || [])
    .map((reason) => {
      const code = (reason.code || "").split(":")[0].trim().toLowerCase();

      return (
        UNJUDGED_REASONS[code] ??
        (reason.message ? reason.message.toLowerCase() : undefined)
      );
    })
    .filter(Boolean);

  // two codes can read the same once mapped, so de-duplicate the labels
  return Array.from(new Set(labels)).slice(0, MAX_UNJUDGED_REASONS);
}

/**
 * A headline with the translatable reasons to put into its `{{reason}}`;
 * each is translated on its own, then joined (see `labelReason`).
 */
export interface IDeepfakePartialLabel {
  key: string;
  reasons: string[];
}

/**
 * The headline for a clean verdict that covers only part of the check.
 *
 * A video clip whose participant barely spoke comes back as video AUTHENTIC
 * next to audio NOT_APPLICABLE, and the service's headline (the most severe
 * scan) is AUTHENTIC — so the badge would say "No signs of manipulation" about
 * a voice nobody analysed. This names the half that was not checked, and why
 * when RD said. `undefined` when every scan was judged, or when none was (the
 * plain NOT_APPLICABLE / UNABLE_TO_EVALUATE labels already say that).
 */
export function partialCleanLabel(
  results: IDeepfakeScanResult[] | undefined
): IDeepfakePartialLabel | undefined {
  if (!results || results.length < 2) return undefined;

  const unjudged = results.filter(isUnjudged);

  if (!unjudged.length || unjudged.length === results.length) return undefined;

  const voice = unjudged.find((result) => result.kind === "audio");
  const reasons = unjudgedReasons(voice ?? unjudged[0]);

  if (voice) {
    return reasons.length
      ? {
          key: "No signs of manipulation in the picture; the voice could not be checked ({{reason}})",
          reasons
        }
      : {
          key: "No signs of manipulation in the picture; the voice could not be checked",
          reasons
        };
  }

  return reasons.length
    ? {
        key: "No signs of manipulation in the voice; the picture could not be checked ({{reason}})",
        reasons
      }
    : {
        key: "No signs of manipulation in the voice; the picture could not be checked",
        reasons
      };
}

const KIND_LABELS: Record<IDeepfakeScanResult["kind"], string> = {
  image: "Image",
  audio: "Audio",
  video: "Video"
};

/** A translatable fact about a scan, e.g. "4 of 5 models agree". */
export interface IDeepfakeFact {
  key: string;
  params?: Record<string, string | number>;
}

/**
 * One tooltip block per scan. For a single-media check the verdict is the
 * headline already, so `kind`/`text` are omitted and only the details show.
 */
export interface IDeepfakeScanLine {
  kind?: string;
  text?: string;
  /** RD's own explanation (NOT_APPLICABLE reasons), already joined. */
  detail?: string;
  severity: "alert" | "warn" | "muted" | "none";
  /** Neutral context: model agreement, analysed length. */
  facts: IDeepfakeFact[];
  /** Things worth a second look: recognised identity, suspected generator. */
  flags: IDeepfakeFact[];
  /** Caption of the frame; only when the admin opted in. */
  description?: string;
}

/**
 * Per-participant deepfake verdict badge.
 *
 * Deliberately not a real/fake binary: SUSPICIOUS is a first-class outcome, and
 * a check that could not run (quota, plan, timeout, network) is rendered with
 * its own copy so it can never be misread as "this person is synthetic".
 */
@Component({
  selector: "av-deepfake-result",
  templateUrl: "./deepfake-result.component.html",
  styleUrls: ["./deepfake-result.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class DeepfakeResultComponent {
  private deepfakeService = inject(DeepfakeService);
  private translate = inject(TranslateService);

  /**
   * Ticks whenever what `translate.instant` answers can change, as the
   * `translate` pipe does, so a reason translated here follows the language.
   */
  private translations = toSignal(
    merge(
      this.translate.onLangChange,
      this.translate.onTranslationChange,
      this.translate.onDefaultLangChange
    )
  );

  readonly endpointId = input<string>(undefined);
  /** The tile's stream; each of a participant's streams has its own badge. */
  readonly streamId = input<string>(undefined);

  /**
   * The stream's newest check, derived from the service's checks and this
   * tile's inputs. Every way a check changes lands in that one signal — this
   * page's own events and another agent's verdict arriving through the ai's
   * list alike — and a tile reused for another stream re-selects at once.
   */
  check = computed<IDeepfakeCheck>(() =>
    this.deepfakeService.getLatestCheckForStream(
      this.endpointId(),
      this.streamId()
    )
  );

  isRunning = computed(() =>
    DEEPFAKE_PENDING_STATES.includes(this.check()?.state)
  );

  /** A clip check holding its recording for the participant's voice. */
  isListening = computed(() => this.check()?.state === "listening");

  /**
   * What the check is doing right now. The steps before the verdict all take
   * visible time — holding for a voice, recording a clip, uploading it, then
   * Reality Defender's own analysis — so the tooltip names the current one
   * instead of one generic line for all of them.
   */
  runningLabel = computed(() =>
    runningLabel(this.check()?.state, this.check()?.mediaKind)
  );

  isVerdict = computed(() => this.check()?.state === "verdict");

  /** true for the two states where we have no verdict at all. */
  isUnavailable = computed(() =>
    ["error", "timeout"].includes(this.check()?.state)
  );

  status = computed(() => this.check()?.status as DeepfakeStatusEnum);

  /** 0-100. Higher means more likely synthetic. */
  score = computed(() => this.check()?.score);

  hasScore = computed(
    () => this.score() !== null && this.score() !== undefined
  );

  /**
   * A clean verdict on part of the check only (see `partialCleanLabel`). Not
   * a success: the badge drops the tick so it cannot be read as "all clear".
   */
  partialLabel = computed(() =>
    this.status() === DeepfakeStatusEnum.authentic
      ? partialCleanLabel(this.check()?.results)
      : undefined
  );

  icon = computed(() => {
    if (this.partialLabel()) return "info-round";

    switch (this.status()) {
      case DeepfakeStatusEnum.authentic:
        return "tick-round-success";
      case DeepfakeStatusEnum.fake:
      case DeepfakeStatusEnum.suspicious:
        return "alert";
      default:
        return "info-round";
    }
  });

  /** The prominent label. Never "real" / "fake" alone. */
  label = computed(
    () => this.partialLabel()?.key ?? verdictLabel(this.status())
  );

  /**
   * Why part of the check went unjudged, for the `{{reason}}` in `label`:
   * each reason translated, then joined, since the joined text is no key.
   */
  labelReason = computed(() => {
    this.translations();

    const reasons = this.partialLabel()?.reasons ?? [];

    return reasons.length
      ? reasons.map((reason) => this.translate.instant(reason)).join(", ")
      : undefined;
  });

  /** Distinct copy per failure reason — an operational problem is not a verdict. */
  unavailableLabel = computed(() => unavailableLabel(this.check()?.reason));

  /**
   * One block per scan. With more than one media the block names the scan
   * and its own verdict, so the agent sees that "Likely synthetic" came from
   * the audio while the image was clean — the headline alone would hide that.
   * With one media the headline already says it, so the block carries only
   * the details (why NOT_APPLICABLE, model agreement, analysed length,
   * context flags, the opt-in frame description).
   */
  scanLines = computed<IDeepfakeScanLine[]>(() => {
    const results = this.check()?.results;

    if (!results?.length) return [];

    const named = results.length > 1;

    return results
      .map((result) => ({
        ...(named
          ? {
              kind: KIND_LABELS[result.kind] ?? result.kind,
              text: result.status
                ? result.score !== null && result.score !== undefined
                  ? `${verdictLabel(result.status)} (${result.score}/100)`
                  : verdictLabel(result.status)
                : unavailableLabel(result.reason)
            }
          : {}),
        detail: joinReasons(result),
        severity: severityOf(result),
        facts: factsOf(result),
        flags: flagsOf(result),
        description: result.context?.description
      }))
      .filter(
        (line) =>
          line.kind ||
          line.detail ||
          line.facts.length ||
          line.flags.length ||
          line.description
      );
  });
}

function joinReasons(result: IDeepfakeScanResult): string | undefined {
  const messages = (result.reasons || [])
    .map((r) => r.message || r.code)
    .filter(Boolean);

  return messages.length ? messages.join("; ") : undefined;
}

function severityOf(
  result: IDeepfakeScanResult
): IDeepfakeScanLine["severity"] {
  switch (result.status as DeepfakeStatusEnum) {
    case DeepfakeStatusEnum.fake:
      return "alert";
    case DeepfakeStatusEnum.suspicious:
      return "warn";
    case DeepfakeStatusEnum.authentic:
      return "none";
    default:
      return "muted";
  }
}

function factsOf(result: IDeepfakeScanResult): IDeepfakeFact[] {
  const facts: IDeepfakeFact[] = [];

  if (result.agreement?.total) {
    facts.push({
      key: "{{agreeing}} of {{total}} models agree",
      params: result.agreement
    });
  }

  if (result.analysedSeconds) {
    facts.push({
      key: "{{seconds}} s analysed",
      params: { seconds: result.analysedSeconds }
    });
  }

  return facts;
}

function flagsOf(result: IDeepfakeScanResult): IDeepfakeFact[] {
  const context = result.context;
  const flags: IDeepfakeFact[] = [];

  if (!context) return flags;

  if (context.recognizableIdentities?.length) {
    flags.push({
      key: "Resembles a public figure: {{names}}",
      params: { names: context.recognizableIdentities.join(", ") }
    });
  }

  if (context.potentialSource) {
    flags.push({
      key: "Possible source: {{source}}",
      params: { source: context.potentialSource }
    });
  }

  if (context.manipulationReasons?.length) {
    flags.push({
      key: "Signals: {{signals}}",
      params: {
        signals: context.manipulationReasons
          .map((reason) => reason.toLowerCase().replace(/_/g, " "))
          .join(", ")
      }
    });
  }

  return flags;
}
